import type { AuthMeResponse } from '@atlas/api-contract';

import { apiClient } from './client';

/**
 * `/v1/auth/*` — the session endpoints introduced with Phase 2.
 *
 * Thin, like every other request module: the axios interceptor normalises
 * failures into `ApiError` and React Query owns caching. The session lives in an
 * `HttpOnly` cookie the browser attaches automatically (the client is created
 * with `withCredentials`), so none of these functions touch a token.
 *
 * The OAuth redirect that starts a session is deliberately *not* here — it is a
 * full-page navigation, not an XHR, and lives in `features/auth/sign-in.ts`.
 */

/** Request options every read accepts, so React Query can cancel it. */
interface ReadOptions {
  signal?: AbortSignal | undefined;
}

const withSignal = ({ signal }: ReadOptions) =>
  signal === undefined ? {} : { signal };

/**
 * `GET /v1/auth/me` — the signed-in user, or `401` when there is no session.
 *
 * The 401 is left to reject: the query layer treats it as "unauthenticated" and
 * the axios interceptor has already tried exactly one refresh before it got
 * here (spec R7.5).
 */
export const getMe = async (
  options: ReadOptions = {},
): Promise<AuthMeResponse> => {
  const { data } = await apiClient.get<AuthMeResponse>(
    '/auth/me',
    withSignal(options),
  );

  return data;
};

/** `POST /v1/auth/refresh` — mint a new access cookie from the refresh cookie. */
export const refreshSession = async (): Promise<void> => {
  await apiClient.post('/auth/refresh');
};

/** `POST /v1/auth/logout` — clear the session cookies server-side. */
export const logout = async (): Promise<void> => {
  await apiClient.post('/auth/logout');
};
